import api from './api';
import {
  Building,
  BuildingDocument,
  CreateBuildingRequest,
  UpdateBuildingRequest,
  Meter,
  CreateMeterRequest,
  UpdateMeterRequest,
  BuildingNote,
  CreateNoteRequest,
  UpdateNoteRequest,
} from '../types';

const buildingService = {
  getAll: async (): Promise<Building[]> => {
    const response = await api.get<Building[]>('/buildings');
    return response.data;
  },

  getById: async (id: number): Promise<Building> => {
    const response = await api.get<Building>(`/buildings/${id}`);
    return response.data;
  },

  create: async (data: CreateBuildingRequest): Promise<Building> => {
    const response = await api.post<Building>('/buildings', data);
    return response.data;
  },

  update: async (id: number, data: UpdateBuildingRequest): Promise<Building> => {
    const response = await api.put<Building>(`/buildings/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/buildings/${id}`);
  },

  getMeters: async (buildingId: number): Promise<Meter[]> => {
    const response = await api.get<Meter[]>(`/buildings/${buildingId}/meters`);
    return response.data;
  },

  createMeter: async (buildingId: number, data: CreateMeterRequest): Promise<Meter> => {
    const response = await api.post<Meter>(`/buildings/${buildingId}/meters`, data);
    return response.data;
  },

  updateMeter: async (meterId: number, data: UpdateMeterRequest): Promise<Meter> => {
    const response = await api.put<Meter>(`/buildings/meters/${meterId}`, data);
    return response.data;
  },

  deleteMeter: async (meterId: number): Promise<void> => {
    await api.delete(`/buildings/meters/${meterId}`);
  },

  getNotes: async (buildingId: number): Promise<BuildingNote[]> => {
    const response = await api.get<BuildingNote[]>(`/buildings/${buildingId}/notes`);
    return response.data;
  },

  createNote: async (buildingId: number, data: CreateNoteRequest): Promise<BuildingNote> => {
    const response = await api.post<BuildingNote>(`/buildings/${buildingId}/notes`, data);
    return response.data;
  },

  updateNote: async (noteId: number, data: UpdateNoteRequest): Promise<BuildingNote> => {
    const response = await api.put<BuildingNote>(`/buildings/notes/${noteId}`, data);
    return response.data;
  },

  deleteNote: async (noteId: number): Promise<void> => {
    await api.delete(`/buildings/notes/${noteId}`);
  },

  getDocuments: async (buildingId: number): Promise<BuildingDocument[]> => {
    const response = await api.get<BuildingDocument[]>(`/buildings/${buildingId}/documents`);
    return response.data;
  },

  uploadDocument: async (
    buildingId: number,
    file: File,
    name: string,
    category?: string,
  ): Promise<BuildingDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    if (category) formData.append('category', category);
    const response = await api.post<BuildingDocument>(
      `/buildings/${buildingId}/documents`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  fetchDocumentBlob: async (docId: number): Promise<{ blob: Blob; fileName: string }> => {
    const response = await api.get(`/buildings/documents/${docId}/file`, {
      responseType: 'blob',
    });
    const disposition: string = response.headers['content-disposition'] ?? '';
    const match = disposition.match(/filename="?([^";\n]+)"?/i);
    const fileName = match?.[1] ?? `document-${docId}`;
    return { blob: response.data, fileName };
  },

  downloadDocument: async (docId: number, fileName: string): Promise<void> => {
    const response = await api.get(`/buildings/documents/${docId}/file`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  deleteDocument: async (docId: number): Promise<void> => {
    await api.delete(`/buildings/documents/${docId}`);
  },
};

export default buildingService;
